import { action, autorun, makeObservable, observable, reaction } from 'mobx';

import { analytics } from '../../services/analytics';
import { getRequest } from '../../services/api';
import { AUTH_FLOW_KEY } from '../../services/auth';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError, devLog } from '../../utils/log';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import { type LandingData } from './types';

export class LandingStore extends Loadable {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);

    this.resolveAndUpdateFlags();

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('landing_page_v2' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    reaction(
      () => this.rootStore.authStore.authStatus,
      (authStatus) => {
        if (authStatus === 'authenticated') {
          this.setLandingPageEventSent(false);
        }
      },
      { fireImmediately: true },
    );

    const resetLandingPageEvent = () => {
      this.setLandingPageEventSent(false);
    };

    window.addEventListener('popstate', resetLandingPageEvent);

    const originalPushState = history.pushState.bind(history);
    history.pushState = (...args) => {
      originalPushState(...args);
      resetLandingPageEvent();
    };

    const originalReplaceState = history.replaceState.bind(history);
    history.replaceState = (...args) => {
      originalReplaceState(...args);
      resetLandingPageEvent();
    };

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone) {
        this.resolveAndUpdateFlags();
        if (!this.rootStore.authStore.authorized && this.landingV2Flag && !this.landingData && !this.error) {
          void this.fetchLandingData();
        }
      }
    });

    if (this.landingV2Flag && !this.landingData && !this.rootStore.authStore.authorized) {
      void this.fetchLandingData();
    }
  }

  private resolveAndUpdateFlags() {
    const landingV2FF = resolveFlag('landing_page_v2', true);
    this.setLandingV2Flag(landingV2FF);
  }

  @observable
  public landingV2Flag = false;

  @action
  public setLandingV2Flag(flag: boolean) {
    this.landingV2Flag = flag;
  }

  @observable
  public landingData: LandingData | null = null;

  @action
  public setLandingData(data: LandingData) {
    this.landingData = data;
  }

  @observable
  public landingPageEventSent: boolean = false;

  @action
  public setLandingPageEventSent(sent: boolean) {
    this.landingPageEventSent = sent;
  }

  public sendLandingPageEvent(landingType: string, additionalParams?: Record<string, any>) {
    if (this.landingPageEventSent) {
      return;
    }
    sessionStorage.setItem(AUTH_FLOW_KEY, 'true');
    void analytics.sendEvent('landing_page', {
      event_action: 'impression',
      event_category: 'sign_up',
      landing_type: landingType,
      utm_promo: this.rootStore.authStore.getUtmPromo(),
      utm_feature: this.rootStore.authStore.getUtmFeature(),
      experiments: {
        signup_redesign: resolveFlag('ui_redesign_registration_flow', false),
      },
      ...(additionalParams ?? {}),
    });
    this.setLandingPageEventSent(true);
  }

  async fetchLandingData() {
    try {
      if (this.loading) {
        devLog('LandingStore: Already loading landing data, skipping fetch');
        return;
      }

      this.setLoading(true);

      if (!this.landingV2Flag) {
        devLog('LandingStore: Landing V2 flag is disabled, skipping fetchLandingData');
        return;
      }

      if (this.landingData) {
        devLog('LandingStore: Landing data already exists, skipping fetch');
        return;
      }

      const response = await getRequest<ApiResponse<LandingData, 'landingData'>>('landing');

      if (response.data.landingData) {
        this.setLandingData(response.data.landingData);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      this.setError(errorMessage);
      devError(`Failed to fetch landing data: ${errorMessage}`);
    } finally {
      this.setLoading(false);
    }
  }
}
